import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getSignedS3Url, selectSignedUrl } from '../slices/voiceActorSlice';
import { HTTP_METHODS, RESOURCE_TYPES } from '../apiEngine/schemas/parameterSchema';

/**
 * Hook for getting signed S3 URLs using the simplified approach
 * This hook provides a simple way to get signed URLs for S3 objects
 * without any complex parsing or reconstruction of URLs.
 * 
 * Uses the apiThunkCreator system for standardized API operations.
 * 
 * @returns {Object} An object with the getSignedUrl function
 */
export const useSignedS3Url = () => {
  const dispatch = useDispatch();
  // We need to access the Redux state outside the callback
  const state = useSelector(state => state);

  /**
   * Get a signed URL for an S3 object
   * 
   * @param {string} url - The S3 URL to get a signed URL for
   * @param {string} userId - The ID of the user
   * @returns {Promise<string>} A promise that resolves to the signed URL
   */
  const getSignedUrl = useCallback(async (url, userId) => {
    if (!url || !userId) {
      console.error('getSignedUrl: Missing url or userId parameter');
      return '';
    }

    // Check if we already have this URL in the cache
    // Use the state we captured outside the callback
    const cachedUrl = selectSignedUrl(state, url, userId);

    // console.debug('getSignedUrl: userId', userId, 'cachedUrl', cachedUrl);

    if (cachedUrl) {
      return cachedUrl;
    }

    // If not in cache, fetch it
    try {
      const result = await dispatch(getSignedS3Url({ url, userId })).unwrap();
      return result.signedUrl;
    } catch (error) {
      console.error('Error getting signed URL:', error);
      return '';
    }
  }, [dispatch, state]); // Add state to the dependency array

  return { getSignedUrl };
};

export default useSignedS3Url;
